// ============================================================================
// RETURN INTERFACE SYSTEM
// ============================================================================
// CONFIGURATION - Reads from global config set in theme.liquid
const RETURN_INTERFACE_API_BASE_URL = window.__BOOKBUGS_CONFIG?.API_BASE_URL || 'https://order-return-testing-cjlu.onrender.com/api';
// ============================================================================

// Return Interface Manager
(function() {
  'use strict';

  if (typeof window.ReturnInterface === 'undefined') {
    window.ReturnInterface = class ReturnInterface {
      constructor() {
        this.apiBaseUrl = RETURN_INTERFACE_API_BASE_URL;
        this.customerId = null;
        this.activeRentals = [];
        this.selectedBooks = [];
        this.planInfo = null;
        this.isSubmitting = false;
        this.isLoading = false;
        
        // Return request state
        this.currentReturnRequest = null;
        this.returnRequestId = null;
        
        this.init();
      }

      async init() {
        try {
          console.log('🔄 Initializing Return Interface...');
          
          // First, test backend connectivity
          await this.testBackendConnectivity();
          
          // Get customer ID
          this.customerId = this.getCustomerId();
          if (!this.customerId || this.customerId === 'guest') {
            console.warn('⚠️ No customer ID found, cannot load return interface');
            this.showError('Please log in to access the return interface.');
            return;
          }
          
          console.log('👤 Customer ID:', this.customerId);
          
          // Load active rentals
          await this.loadActiveRentals();
          
          // Initialize UI
          this.initializeInterface();
          this.setupEventListeners();
          
          console.log('✅ Return Interface initialized successfully');
        } catch (error) {
          console.error('❌ Error initializing return interface:', error);
          this.showError('Failed to initialize return interface. Please refresh the page.');
        }
      }

      async testBackendConnectivity() {
        console.log('🌐 Testing backend connectivity...');
        
        try {
          const healthCheckUrl = this.apiBaseUrl.replace('/api', '') + '/health';
          console.log('🚑 Health check URL:', healthCheckUrl);
          
          const response = await fetch(healthCheckUrl, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json'
            }
          });
          
          console.log('🚑 Health check response status:', response.status);
          
          if (response.ok) {
            const data = await response.json();
            console.log('✅ Backend server is responding:', data);
          } else {
            console.warn('⚠️ Backend server health check failed:', response.status, response.statusText);
          }
        } catch (error) {
          console.error('❌ Backend connectivity test failed:', error.message);
          console.error('❌ This suggests the backend server is not running or not accessible');
          console.error('❌ Expected backend URL:', this.apiBaseUrl);
          
          // Test if API base route exists
          try {
            const apiTestUrl = this.apiBaseUrl + '/health-check';
            console.log('🧪 Testing API base route:', apiTestUrl);
            const apiResponse = await fetch(apiTestUrl);
            console.log('🧪 API route test status:', apiResponse.status);
            if (apiResponse.ok) {
              const apiData = await apiResponse.json();
              console.log('✅ API base route is working:', apiData);
            }
          } catch (apiError) {
            console.error('❌ API base route test failed:', apiError.message);
          }
        }
      }

      getCustomerId() {
        // Method 1: Try to get customer ID from various sources
        const container = document.querySelector('.return-interface-container, .book-rental-container');
        if (container && container.dataset.customerId) {
          return container.dataset.customerId;
        }
        
        // Method 2: Check for customer ID in meta tags
        const metaCustomerId = document.querySelector('meta[name="customer-id"]');
        if (metaCustomerId) {
          return metaCustomerId.content;
        }
        
        // Method 3: Check URL parameters
        const urlParams = new URLSearchParams(window.location.search);
        const urlCustomerId = urlParams.get('customer_id');
        if (urlCustomerId) {
          return urlCustomerId;
        }
        
        // Method 4: Check for global customer variable
        if (typeof window.customerData !== 'undefined' && window.customerData.id) {
          return window.customerData.id.toString();
        }
        
        console.log('👤 No customer ID found');
        return null;
      }

      async loadActiveRentals() {
        try {
          this.setLoadingState(true);
          console.log('📚 Loading active rentals for customer:', this.customerId);
          console.log('🌐 Making request to:', `${this.apiBaseUrl}/customer/${this.customerId}/active-rentals`);
          
          const response = await fetch(`${this.apiBaseUrl}/customer/${this.customerId}/active-rentals`);
          console.log('📡 Response status:', response.status, response.statusText);
          console.log('📡 Response headers:', Object.fromEntries(response.headers.entries()));
          
          const data = await response.json();
          console.log('📦 Response data:', data);
          
          if (!response.ok) {
            console.error('❌ API Error - Status:', response.status);
            console.error('❌ API Error - Data:', data);
            
            // Provide more specific error messages based on status
            let errorMessage = 'Failed to load active rentals';
            if (response.status === 404) {
              errorMessage = 'API endpoint not found. Please check if the backend server is running.';
              console.error('❌ 404 Error - Possible causes:');
              console.error('   1. Backend server not deployed or not running');
              console.error('   2. Incorrect API URL:', this.apiBaseUrl);
              console.error('   3. Route not properly registered');
              console.error('   4. Environment variables not set correctly');
            } else if (response.status === 500) {
              errorMessage = 'Server error. Please check the backend logs.';
            } else if (response.status === 403) {
              errorMessage = 'Access forbidden. Please check authentication.';
            }
            
            throw new Error(data.error || errorMessage);
          }
          
          this.activeRentals = data.active_rentals || [];
          this.planInfo = data.plan_info || { current_plan: 'Plan A', total_slots: 5, used_slots: 0, available_slots: 5 };
          
          console.log('📚 Active rentals loaded:', {
            count: this.activeRentals.length,
            plan: this.planInfo.current_plan,
            slots: `${this.planInfo.used_slots}/${this.planInfo.total_slots}`
          });
          
          this.renderActiveRentals();
          this.updatePlanInfo();
          
        } catch (error) {
          console.error('❌ Error loading active rentals:', error);
          this.showError('Failed to load your active rentals. Please refresh the page.');
        } finally {
          this.setLoadingState(false);
        }
      }

      initializeInterface() {
        // Work with the existing return interface container from the Liquid template
        const container = document.querySelector('.return-interface-container');
        if (!container) {
          console.error('❌ Return interface container not found. Make sure the page template is loaded.');
          this.showError('Return interface not found. Please refresh the page.');
          return;
        }
        
        console.log('✅ Using existing return interface structure');
        
        // The HTML structure already exists from the Liquid template
        // We just need to ensure all elements are properly initialized
        this.ensureFormElements();
      }
      
      ensureFormElements() {
        // Make sure the minimum date is set correctly
        const dateInput = document.querySelector('#collection-date');
        if (dateInput) {
          dateInput.min = new Date().toISOString().split('T')[0];
        }
        
        // Initialize any other form elements that need dynamic setup
        console.log('📝 Form elements initialized');
      }

      renderActiveRentals() {
        const grid = document.querySelector('.active-rentals-grid');
        if (!grid) {
          console.error('❌ Active rentals grid not found');
          return;
        }
        
        if (this.activeRentals.length === 0) {
          grid.innerHTML = `
            <div class="no-rentals">
              <div class="no-rentals-icon">📚</div>
              <h4>No Active Rentals</h4>
              <p>You don't have any books to return at the moment.</p>
              <a href="/collections/all-books" class="btn btn-primary">Browse Books</a>
            </div>
          `;
          return;
        }
        
        const booksHtml = this.activeRentals.map(book => `
          <div class="rental-book-card" data-variant-id="${book.variant_id}" data-rental-id="${book.rental_record_id}">
            <div class="book-selection">
              <input type="checkbox" class="book-checkbox" id="book-${book.variant_id}" 
                     data-variant-id="${book.variant_id}" data-title="${book.title}">
              <label for="book-${book.variant_id}" class="book-checkbox-label"></label>
            </div>
            <div class="book-image">
              <img src="${book.image_url || '/assets/book-placeholder.png'}" alt="${book.title}" loading="lazy">
            </div>
            <div class="book-details">
              <h4 class="book-title">${book.title}</h4>
              <p class="rental-date">Rented: ${new Date(book.rented_date).toLocaleDateString()}</p>
              <p class="days-rented">${book.days_rented} days ago</p>
            </div>
          </div>
        `).join('');
        
        grid.innerHTML = booksHtml;
        console.log('📚 Rendered', this.activeRentals.length, 'active rentals');
      }

      updatePlanInfo() {
        if (!this.planInfo) return;
        
        // Map plan letters to full plan names
        const planMappings = {
          'Plan A': 'The Buzzy Beginners (3mo)',
          'Plan B': 'The Bug Buddies (3mo)',
          'Plan C': 'The Buzzy Beginners (6mo)',
          'Plan D': 'The Bug Buddies (6mo)',
          'Plan E': 'The Buzzy Beginners (annual)',
          'Plan F': 'The Bug Buddies (annual)'
        };
        
        const planName = document.querySelector('.plan-name');
        const planUsage = document.querySelector('.plan-usage');
        const availableSlots = document.querySelector('.available-slots');
        
        if (planName) {
          const fullPlanName = planMappings[this.planInfo.current_plan] || this.planInfo.current_plan;
          planName.textContent = fullPlanName;
        }
        if (planUsage) planUsage.textContent = `${this.planInfo.used_slots} / ${this.planInfo.total_slots} books`;
        if (availableSlots) {
          const slots = this.planInfo.available_slots;
          availableSlots.textContent = `${slots} available slot${slots !== 1 ? 's' : ''}`;
          availableSlots.className = `available-slots ${slots > 0 ? 'has-slots' : 'no-slots'}`;
        }
      }

      setupEventListeners() {
        // Book selection checkboxes
        document.addEventListener('change', (e) => {
          if (e.target.classList.contains('book-checkbox')) {
            this.handleBookSelection(e.target);
          }
        });
        
        // Make entire book card clickable
        document.addEventListener('click', (e) => {
          const bookCard = e.target.closest('.rental-book-card');
          if (bookCard) {
            // Don't trigger if clicking directly on checkbox or label
            if (e.target.classList.contains('book-checkbox') || 
                e.target.classList.contains('book-checkbox-label')) {
              return;
            }
            
            // Find the checkbox within this card and toggle it
            const checkbox = bookCard.querySelector('.book-checkbox');
            if (checkbox) {
              checkbox.checked = !checkbox.checked;
              // Trigger the change event manually
              checkbox.dispatchEvent(new Event('change', { bubbles: true }));
            }
          }
        });
        
        // Return form submission
        const form = document.querySelector('.return-request-form');
        if (form) {
          form.addEventListener('submit', (e) => {
            e.preventDefault();
            this.handleReturnFormSubmission();
          });
        }
        
        // Cancel return button
        const cancelBtn = document.querySelector('.cancel-return');
        if (cancelBtn) {
          cancelBtn.addEventListener('click', () => {
            this.resetReturnForm();
          });
        }
        
        // Status tracking button
        document.addEventListener('click', (e) => {
          if (e.target.classList.contains('track-return')) {
            this.trackReturnStatus();
          }
          
          if (e.target.classList.contains('start-new-return')) {
            this.startNewReturn();
          }
          
          if (e.target.classList.contains('retry-button')) {
            this.hideError();
            this.loadActiveRentals();
          }
        });
      }

      handleBookSelection(checkbox) {
        const variantId = checkbox.dataset.variantId;
        const title = checkbox.dataset.title;
        const bookCard = checkbox.closest('.rental-book-card');
        
        if (checkbox.checked) {
          // Add to selected books
          if (!this.selectedBooks.find(book => book.variant_id === variantId)) {
            const bookData = this.activeRentals.find(book => book.variant_id === variantId);
            if (bookData) {
              this.selectedBooks.push({
                variant_id: variantId,
                product_id: bookData.product_id,
                title: title,
                image_url: bookData.image_url,
                rental_record_id: bookData.rental_record_id
              });
            }
          }
          // Add selected class to book card
          if (bookCard) {
            bookCard.classList.add('selected');
          }
        } else {
          // Remove from selected books
          this.selectedBooks = this.selectedBooks.filter(book => book.variant_id !== variantId);
          // Remove selected class from book card
          if (bookCard) {
            bookCard.classList.remove('selected');
          }
        }
        
        this.updateReturnFormVisibility();
        this.updateSelectedBooksSummary();
        
        console.log('📚 Selected books for return:', this.selectedBooks.length);
      }

      clearSelection() {
        // Clear all selected books
        this.selectedBooks = [];
        
        // Uncheck all checkboxes and remove selected class from cards
        document.querySelectorAll('.book-checkbox').forEach(checkbox => {
          checkbox.checked = false;
          const bookCard = checkbox.closest('.rental-book-card');
          if (bookCard) {
            bookCard.classList.remove('selected');
          }
        });
        
        // Hide the return action section
        this.updateReturnFormVisibility();
        
        // Update the summary
        this.updateSelectedBooksSummary();
      }

      showReturnConfirmation() {
        const bookCount = this.selectedBooks.length;
        const bookText = bookCount === 1 ? 'book' : 'books';
        
        // Create confirmation toast
        const confirmationToast = document.createElement('div');
        confirmationToast.className = 'return-confirmation-toast';
        confirmationToast.innerHTML = `
          <div class="confirmation-content">
            <div class="confirmation-icon">📚</div>
            <div class="confirmation-text">
              <h4>Confirm Return</h4>
              <p>Are you sure you want to return ${bookCount} ${bookText}?</p>
            </div>
            <div class="confirmation-buttons">
              <button class="btn btn-secondary" id="cancel-return-confirm">Cancel</button>
              <button class="btn btn-primary" id="confirm-return-process">Yes, Return</button>
            </div>
          </div>
        `;
        
        // Style the toast
        Object.assign(confirmationToast.style, {
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: 'white',
          border: '2px solid #333333',
          borderRadius: '12px',
          padding: '24px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          zIndex: '10000',
          maxWidth: '400px',
          width: '90%'
        });
        
        document.body.appendChild(confirmationToast);
        
        // Add event listeners
        document.getElementById('cancel-return-confirm').addEventListener('click', () => {
          document.body.removeChild(confirmationToast);
        });
        
        document.getElementById('confirm-return-process').addEventListener('click', () => {
          document.body.removeChild(confirmationToast);
          this.processSimpleReturn();
        });
      }

      async processSimpleReturn() {
        try {
          // Show processing state
          this.showToast('Processing return request...', 'info');
          
          // Group selected books by rental record ID for proper submission
          const booksByRental = {};
          this.selectedBooks.forEach(book => {
            if (!booksByRental[book.rental_record_id]) {
              booksByRental[book.rental_record_id] = [];
            }
            booksByRental[book.rental_record_id].push(book);
          });
          
          console.log('🔍 Debug Info:');
          console.log('Customer ID:', this.customerId);
          console.log('Selected Books:', this.selectedBooks);
          console.log('Books by Rental:', booksByRental);
          console.log('📋 Processing', Object.keys(booksByRental).length, 'separate rental records');
          
          // Process each rental record separately
          const returnResults = [];
          let totalFreedSlots = 0;
          
          for (const [rentalId, books] of Object.entries(booksByRental)) {
            console.log(`📝 Processing rental record: ${rentalId} with ${books.length} books`);
            
            // Prepare return data for this specific rental record
            const returnData = {
              customer_id: this.customerId,
              rental_record_id: rentalId,
              books_to_return: books.map(book => ({
                variant_id: book.variant_id,
                product_id: book.product_id,
                title: book.title,
                image_url: book.image_url
              })),
              return_reason: 'finished_reading',
              preferred_collection_date: null,
              collection_notes: '',
              has_replacement_order: false,
              replacement_order_books: []
            };
            
            console.log(`📝 Submitting return data for rental ${rentalId}:`, returnData);
            console.log('🌍 API URL:', `${this.apiBaseUrl}/return-requests`);
            
            // Submit return request for this rental record
            const response = await fetch(`${this.apiBaseUrl}/return-requests`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify(returnData)
            });
            
            console.log(`📡 Response status for rental ${rentalId}:`, response.status, response.statusText);
            console.log('📡 Response headers:', Object.fromEntries(response.headers.entries()));
            
            const result = await response.json();
            console.log(`📦 Response data for rental ${rentalId}:`, result);
            
            if (!response.ok) {
              console.error(`❌ API Error for rental ${rentalId}:`);
              console.error('Status:', response.status);
              console.error('Status Text:', response.statusText);
              console.error('Error Message:', result.error);
              console.error('Full Response:', result);
              
              throw new Error(`Failed to process rental ${rentalId}: ${result.error || `API Error ${response.status}`}`);
            }
            
            returnResults.push({
              rentalId,
              books: books.length,
              result
            });
            
            totalFreedSlots += result.freed_slots || books.length;
          }
          
          console.log('✅ All return requests processed successfully:', returnResults);
          
          // Success!
          const totalBooks = returnResults.reduce((sum, result) => sum + result.books, 0);
          const totalRecords = returnResults.length;
          
          let successMessage;
          if (totalRecords === 1) {
            successMessage = `Return request submitted successfully! 📚 (${totalBooks} book${totalBooks > 1 ? 's' : ''})`;
          } else {
            successMessage = `Return requests submitted successfully! 📚 (${totalBooks} books from ${totalRecords} rental records)`;
          }
          
          this.showToast(successMessage, 'success');
          
          // Clear selection and refresh
          this.clearSelection();
          await this.loadActiveRentals();
          
          // Update plan info
          if (this.planInfo) {
            this.planInfo.available_slots += totalFreedSlots;
            this.planInfo.used_slots -= totalFreedSlots;
            this.updatePlanInfo();
          }
          
        } catch (error) {
          console.error('❌ Detailed Error Information:');
          console.error('Error Type:', error.constructor.name);
          console.error('Error Message:', error.message);
          console.error('Error Stack:', error.stack);
          console.error('API Base URL:', this.apiBaseUrl);
          console.error('Customer ID:', this.customerId);
          console.error('Selected Books Count:', this.selectedBooks.length);
          
          let userMessage = 'Failed to process return. Please try again.';
          
          // Provide more specific error messages
          if (error.message.includes('Failed to fetch')) {
            userMessage = 'Cannot connect to server. Please check your internet connection.';
          } else if (error.message.includes('404')) {
            userMessage = 'Return service unavailable. Please contact support.';
          } else if (error.message.includes('500')) {
            userMessage = 'Server error. Please try again in a few minutes.';
          }
          
          this.showToast(userMessage, 'error');
        }
      }

      updateReturnFormVisibility() {
        const formSection = document.querySelector('.return-form-section');
        let returnActionSection = document.querySelector('.return-action-section');
        
        // Hide the full form
        if (formSection) {
          formSection.style.display = 'none';
        }
        
        // Create or show simple return button section
        if (this.selectedBooks.length > 0) {
          if (!returnActionSection) {
            // Create the return action section
            const activeRentalsSection = document.querySelector('.active-rentals-section');
            returnActionSection = document.createElement('div');
            returnActionSection.className = 'return-action-section';
            returnActionSection.innerHTML = `
              <div class="selected-books-summary">
                <h4>Books Selected for Return (<span class="selected-count">0</span>)</h4>
                <div class="selected-books-list">
                  <p class="no-selection">No books selected</p>
                </div>
              </div>
              <div class="return-action-buttons">
                <button type="button" class="btn btn-secondary" id="clear-selection">Clear Selection</button>
                <button type="button" class="btn btn-primary" id="process-return">Return Selected Books</button>
              </div>
            `;
            activeRentalsSection.appendChild(returnActionSection);
            
            // Add event listeners for the new buttons
            document.getElementById('clear-selection').addEventListener('click', () => {
              this.clearSelection();
            });
            
            document.getElementById('process-return').addEventListener('click', () => {
              this.showReturnConfirmation();
            });
          }
          
          returnActionSection.style.display = 'block';
          this.updateSelectedBooksSummary();
        } else {
          if (returnActionSection) {
            returnActionSection.style.display = 'none';
          }
        }
      }

      updateSelectedBooksSummary() {
        const countElement = document.querySelector('.selected-count');
        const listElement = document.querySelector('.selected-books-list');
        
        if (countElement) {
          countElement.textContent = this.selectedBooks.length;
        }
        
        if (listElement) {
          if (this.selectedBooks.length === 0) {
            listElement.innerHTML = '<p class="no-selection">No books selected</p>';
          } else {
            const booksHtml = this.selectedBooks.map(book => `
              <div class="selected-book-item">
                <img src="${book.image_url}" alt="${book.title}" class="selected-book-image">
                <span class="selected-book-title">${book.title}</span>
              </div>
            `).join('');
            listElement.innerHTML = booksHtml;
          }
        }
      }
      
      // =============================================
      // RENTAL CART INTEGRATION METHODS
      // =============================================
      
      // Refresh rental cart data after return request
      async refreshRentalCartData() {
        try {
          console.log('🔄 Refreshing rental cart data after return...');
          
          // Check if rental cart is available
          if (typeof window.RentalCart !== 'undefined' && window.rentalCart) {
            // Trigger rental cart to reload current rentals count
            await window.rentalCart.getCurrentRentalsCount();
            
            // Update cart display to reflect new available slots
            window.rentalCart.updateCartDisplay();
            
            console.log('✅ Rental cart data refreshed');
          } else {
            console.log('⚠️ Rental cart not available for refresh');
          }
        } catch (error) {
          console.error('❌ Error refreshing rental cart data:', error);
        }
      }
      
      // Prepare replacement order using rental cart system
      async prepareReplacementOrder() {
        try {
          console.log('🔄 Preparing replacement order integration...');
          
          // Check if rental cart is available
          if (typeof window.RentalCart === 'undefined') {
            console.warn('⚠️ Rental cart system not available, skipping replacement order integration');
            return null;
          }
          
          // Get or create rental cart instance
          let rentalCart = window.rentalCart;
          if (!rentalCart) {
            console.log('🔄 Creating new rental cart instance for replacement order...');
            rentalCart = new window.RentalCart();
            window.rentalCart = rentalCart;
            await rentalCart.init();
          }
          
          // Check if cart has available slots after return
          const freedSlots = this.selectedBooks.length;
          console.log(`📋 Return will free ${freedSlots} slots for replacement books`);
          
          // Show replacement book selection interface
          return await this.showReplacementBookSelection(rentalCart, freedSlots);
          
        } catch (error) {
          console.error('❌ Error preparing replacement order:', error);
          this.showError('Failed to prepare replacement order. You can still submit the return request.');
          return null;
        }
      }
      
      // Show replacement book selection interface
      async showReplacementBookSelection(rentalCart, maxBooks) {
        return new Promise((resolve) => {
          // Create replacement book selection modal
          const modal = document.createElement('div');
          modal.className = 'replacement-books-modal';
          modal.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(0, 0, 0, 0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 10001;
            padding: 20px;
            box-sizing: border-box;
          `;
          
          modal.innerHTML = `
            <div class="replacement-modal-content" style="
              background: white;
              border-radius: 12px;
              max-width: 600px;
              width: 100%;
              max-height: 80vh;
              overflow-y: auto;
              padding: 24px;
            ">
              <div class="replacement-modal-header" style="margin-bottom: 20px;">
                <h3 style="margin: 0 0 8px 0; font-size: 20px; color: #1a202c;">Select Replacement Books</h3>
                <p style="margin: 0; color: #64748b;">We can deliver up to ${maxBooks} new book(s) when collecting your returns</p>
              </div>
              
              <div class="replacement-option-cards" style="margin-bottom: 20px;">
                <div class="option-card" data-option="browse" style="
                  border: 2px solid #e2e8f0;
                  border-radius: 8px;
                  padding: 16px;
                  cursor: pointer;
                  margin-bottom: 12px;
                  transition: border-color 0.2s ease;
                ">
                  <h4 style="margin: 0 0 4px 0; color: #1a202c;">Browse Our Collection</h4>
                  <p style="margin: 0; color: #64748b; font-size: 14px;">Choose from our available books</p>
                </div>
                <div class="option-card" data-option="wishlist" style="
                  border: 2px solid #e2e8f0;
                  border-radius: 8px;
                  padding: 16px;
                  cursor: pointer;
                  margin-bottom: 12px;
                  transition: border-color 0.2s ease;
                ">
                  <h4 style="margin: 0 0 4px 0; color: #1a202c;">From My Wishlist</h4>
                  <p style="margin: 0; color: #64748b; font-size: 14px;">Select from your saved books</p>
                </div>
                <div class="option-card" data-option="recommendations" style="
                  border: 2px solid #e2e8f0;
                  border-radius: 8px;
                  padding: 16px;
                  cursor: pointer;
                  transition: border-color 0.2s ease;
                ">
                  <h4 style="margin: 0 0 4px 0; color: #1a202c;">Recommendations</h4>
                  <p style="margin: 0; color: #64748b; font-size: 14px;">Books similar to what you're returning</p>
                </div>
              </div>
              
              <div class="replacement-message" style="
                background: #f0f4ff;
                border: 1px solid #c7d2fe;
                border-radius: 6px;
                padding: 16px;
                margin-bottom: 20px;
              ">
                <p style="margin: 0; color: #3730a3; font-size: 14px;">
                  ℹ️ For now, you can browse our collection directly and add books to your rental cart. 
                  Your return will free up ${maxBooks} slot(s) for new rentals.
                </p>
              </div>
              
              <div class="replacement-modal-actions" style="display: flex; gap: 12px; justify-content: flex-end;">
                <button class="btn-skip" style="
                  padding: 10px 20px;
                  border: 1px solid #d1d5db;
                  background: white;
                  color: #374151;
                  border-radius: 6px;
                  cursor: pointer;
                  font-size: 14px;
                ">Continue Without Replacement</button>
                <button class="btn-browse" style="
                  padding: 10px 20px;
                  border: none;
                  background: #667eea;
                  color: white;
                  border-radius: 6px;
                  cursor: pointer;
                  font-size: 14px;
                ">Browse Books</button>
              </div>
            </div>
          `;
          
          document.body.appendChild(modal);
          
          // Set up modal functionality
          const optionCards = modal.querySelectorAll('.option-card');
          const skipBtn = modal.querySelector('.btn-skip');
          const browseBtn = modal.querySelector('.btn-browse');
          
          // Option card hover effects
          optionCards.forEach(card => {
            card.addEventListener('mouseenter', () => {
              card.style.borderColor = '#667eea';
              card.style.backgroundColor = '#f8fafc';
            });
            card.addEventListener('mouseleave', () => {
              card.style.borderColor = '#e2e8f0';
              card.style.backgroundColor = 'white';
            });
            card.addEventListener('click', () => {
              const option = card.dataset.option;
              this.handleReplacementOption(option, modal, resolve);
            });
          });
          
          // Button handlers
          skipBtn.addEventListener('click', () => {
            document.body.removeChild(modal);
            resolve(null);
          });
          
          browseBtn.addEventListener('click', () => {
            this.handleReplacementOption('browse', modal, resolve);
          });
          
          // Close on outside click
          modal.addEventListener('click', (e) => {
            if (e.target === modal) {
              document.body.removeChild(modal);
              resolve(null);
            }
          });
        });
      }
      
      // Handle replacement option selection
      handleReplacementOption(option, modal, resolve) {
        document.body.removeChild(modal);
        
        switch (option) {
          case 'browse':
            // Open books collection in new tab and continue with return
            window.open('/collections/all-books', '_blank');
            this.showSuccess('Books collection opened in new tab. Your return request will be processed.');
            resolve(null);
            break;
            
          case 'wishlist':
            // Could integrate with customer wishlist if available
            window.open('/pages/wishlist', '_blank');
            this.showSuccess('Wishlist opened in new tab. Your return request will be processed.');
            resolve(null);
            break;
            
          case 'recommendations':
            // Could show recommendations based on returned books
            window.open('/collections/recommended', '_blank');
            this.showSuccess('Recommendations opened in new tab. Your return request will be processed.');
            resolve(null);
            break;
            
          default:
            resolve(null);
        }
      }
      
      // Create replacement order using rental cart
      async createReplacementOrder(rentalCart, selectedBooks) {
        try {
          console.log('📝 Creating replacement order with rental cart...');
          
          // Add selected books to rental cart
          for (const book of selectedBooks) {
            await rentalCart.addToCart({
              variantId: book.variant_id,
              productId: book.product_id || book.productId || null,
              title: book.title,
              image_url: book.image_url
            });
          }
          
          // Proceed to checkout
          await rentalCart.proceedToCheckout();
          
          return 'replacement_order_created';
        } catch (error) {
          console.error('❌ Error creating replacement order:', error);
          throw error;
        }
      }
      
      // Notify user about coordinated collection and delivery
      showCoordinatedDeliveryMessage(returnRequestId, hasReplacement) {
        const message = hasReplacement 
          ? `Return request submitted! We'll coordinate the collection of your returned books with the delivery of your new rentals.`
          : `Return request submitted! We'll schedule collection of your returned books.`;
          
        this.showSuccess(message);
        
        // Could also show a more detailed coordination message
        if (hasReplacement) {
          setTimeout(() => {
            this.showInfo('Our courier will collect your returns and deliver new books in a single visit. You\'ll receive tracking information once collection is scheduled.');
          }, 2000);
        }
      }

      async handleReturnFormSubmission() {
        if (this.isSubmitting) return;
        
        try {
          this.setSubmittingState(true);
          
          // Validate form
          const formData = this.getFormData();
          if (!this.validateReturnForm(formData)) {
            return;
          }
          
          console.log('📝 Submitting return request:', formData);
          
          // Check if customer wants replacement books and integrate with rental cart
          let replacementOrderUrl = null;
          if (formData.has_replacement_order) {
            replacementOrderUrl = await this.prepareReplacementOrder();
            if (replacementOrderUrl) {
              formData.replacement_checkout_url = replacementOrderUrl;
            }
          }
          
          // Submit return request
          const response = await fetch(`${this.apiBaseUrl}/return-requests`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(formData)
          });
          
          const result = await response.json();
          
          if (!response.ok) {
            throw new Error(result.error || 'Failed to submit return request');
          }
          
          console.log('✅ Return request submitted successfully:', result);
          
          // Store return request info
          this.currentReturnRequest = result;
          this.returnRequestId = result.return_request_id;
          
          // Refresh rental cart to reflect freed slots
          await this.refreshRentalCartData();
          
          // Show coordinated delivery message
          this.showCoordinatedDeliveryMessage(result.return_request_id, formData.has_replacement_order);
          
          // Show success status
          this.showReturnStatus(result);
          
          // Update plan info with freed slots
          if (this.planInfo) {
            this.planInfo.available_slots = result.available_for_new_rentals;
            this.planInfo.used_slots -= result.freed_slots;
            this.updatePlanInfo();
          }
          
        } catch (error) {
          console.error('❌ Error submitting return request:', error);
          this.showError(error.message);
        } finally {
          this.setSubmittingState(false);
        }
      }

      getFormData() {
        // Group selected books by rental record ID for proper submission
        const booksByRental = {};
        this.selectedBooks.forEach(book => {
          if (!booksByRental[book.rental_record_id]) {
            booksByRental[book.rental_record_id] = [];
          }
          booksByRental[book.rental_record_id].push(book);
        });
        
        // For now, we'll use the first rental record (this could be enhanced for multiple records)
        const firstRentalId = Object.keys(booksByRental)[0];
        
        return {
          customer_id: this.customerId,
          rental_record_id: firstRentalId,
          books_to_return: this.selectedBooks.map(book => ({
            variant_id: book.variant_id,
            product_id: book.product_id,
            title: book.title,
            image_url: book.image_url
          })),
          return_reason: document.getElementById('return-reason').value,
          preferred_collection_date: document.getElementById('collection-date').value,
          collection_notes: document.getElementById('collection-notes').value,
          has_replacement_order: document.getElementById('has-replacement').checked,
          replacement_order_books: [] // This could be enhanced to include replacement book selection
        };
      }

      validateReturnForm(formData) {
        if (!formData.return_reason) {
          this.showError('Please select a reason for return.');
          return false;
        }
        
        if (formData.books_to_return.length === 0) {
          this.showError('Please select at least one book to return.');
          return false;
        }
        
        return true;
      }

      showReturnStatus(result) {
        // Hide other sections
        document.querySelector('.active-rentals-section').style.display = 'none';
        document.querySelector('.return-form-section').style.display = 'none';
        
        // Show status section
        const statusSection = document.querySelector('.return-status-section');
        if (statusSection) {
          statusSection.style.display = 'block';
          
          // Update status content
          const requestIdElement = statusSection.querySelector('.request-id');
          const freedSlotsElement = statusSection.querySelector('.freed-slots');
          const availableRentalsElement = statusSection.querySelector('.available-new-rentals');
          
          if (requestIdElement) requestIdElement.textContent = result.return_request_id;
          if (freedSlotsElement) freedSlotsElement.textContent = result.freed_slots;
          if (availableRentalsElement) availableRentalsElement.textContent = result.available_for_new_rentals;
          
          statusSection.scrollIntoView({ behavior: 'smooth' });
        }
      }

      async trackReturnStatus() {
        if (!this.returnRequestId) {
          this.showError('No return request to track.');
          return;
        }
        
        try {
          const response = await fetch(`${this.apiBaseUrl}/return-requests/${this.returnRequestId}/status`);
          const data = await response.json();
          
          if (!response.ok) {
            throw new Error(data.error || 'Failed to fetch return status');
          }
          
          console.log('📊 Return status:', data);
          
          // You could show a detailed status modal here
          alert(`Return Status: ${data.status}\nRequest ID: ${data.return_request_id}`);
          
        } catch (error) {
          console.error('❌ Error tracking return status:', error);
          this.showError('Failed to fetch return status. Please try again.');
        }
      }

      startNewReturn() {
        this.resetReturnForm();
        this.loadActiveRentals();
        
        // Show active rentals section
        document.querySelector('.active-rentals-section').style.display = 'block';
        document.querySelector('.return-status-section').style.display = 'none';
      }

      resetReturnForm() {
        // Clear selected books
        this.selectedBooks = [];
        
        // Uncheck all checkboxes
        document.querySelectorAll('.book-checkbox').forEach(checkbox => {
          checkbox.checked = false;
        });
        
        // Reset form
        const form = document.querySelector('.return-request-form');
        if (form) {
          form.reset();
        }
        
        // Hide form section
        document.querySelector('.return-form-section').style.display = 'none';
        
        this.updateSelectedBooksSummary();
      }

      setLoadingState(isLoading) {
        this.isLoading = isLoading;
        const grid = document.querySelector('.active-rentals-grid');
        
        if (!grid) return;
        
        if (isLoading) {
          grid.innerHTML = `
            <div class="loading-spinner">
              <div class="spinner"></div>
              <p>Loading your rentals...</p>
            </div>
          `;
        }
        // When loading is false, renderActiveRentals() will be called to populate the grid
      }

      setSubmittingState(isSubmitting) {
        this.isSubmitting = isSubmitting;
        const submitBtn = document.querySelector('.submit-return');
        const btnText = submitBtn.querySelector('.btn-text');
        const btnSpinner = submitBtn.querySelector('.btn-spinner');
        
        if (isSubmitting) {
          submitBtn.disabled = true;
          btnText.style.display = 'none';
          btnSpinner.style.display = 'inline';
        } else {
          submitBtn.disabled = false;
          btnText.style.display = 'inline';
          btnSpinner.style.display = 'none';
        }
      }

      showError(message) {
        const errorSection = document.querySelector('.error-section');
        const errorMessage = document.querySelector('.error-message');
        
        if (errorSection && errorMessage) {
          errorMessage.textContent = message;
          errorSection.style.display = 'block';
          errorSection.scrollIntoView({ behavior: 'smooth' });
        } else {
          alert(`Error: ${message}`);
        }
      }
      
      showSuccess(message) {
        this.showToast(message, 'success');
      }
      
      showInfo(message) {
        this.showToast(message, 'info');
      }
      
      showToast(message, type = 'info') {
        const toast = document.createElement('div');
        toast.className = `return-interface-toast ${type}`;
        toast.textContent = message;
        
        Object.assign(toast.style, {
          position: 'fixed',
          top: '20px',
          right: '20px',
          padding: '16px 24px',
          borderRadius: '8px',
          color: 'white',
          zIndex: '10000',
          fontSize: '14px',
          fontWeight: '500',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          transform: 'translateX(100%)',
          transition: 'transform 0.3s ease',
          maxWidth: '400px',
          lineHeight: '1.4'
        });

        if (type === 'success') {
          toast.style.background = '#16a34a';
        } else if (type === 'error') {
          toast.style.background = '#ef4444';
        } else {
          toast.style.background = '#3b82f6';
        }

        document.body.appendChild(toast);
        
        setTimeout(() => {
          toast.style.transform = 'translateX(0)';
        }, 100);

        setTimeout(() => {
          toast.style.transform = 'translateX(100%)';
          setTimeout(() => {
            if (toast.parentNode) {
              toast.parentNode.removeChild(toast);
            }
          }, 300);
        }, type === 'info' ? 5000 : 3000); // Show info messages longer
      }

      hideError() {
        const errorSection = document.querySelector('.error-section');
        if (errorSection) {
          errorSection.style.display = 'none';
        }
      }
    };
  }

  // Auto-initialize only if explicitly requested
  // This prevents conflicts with existing page structures
  
  // Make it available globally for manual initialization
  window.initializeReturnInterface = function() {
    if (!window.returnInterface) {
      console.log('🔄 Manually initializing Return Interface...');
      window.returnInterface = new window.ReturnInterface();
    }
    return window.returnInterface;
  };

})();